import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NotificationRecipient } from '../../types/product';
import { apiRequest } from '../../services/api/axiosConfig';

interface NotificationSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: number;
  canManage: boolean;
  onRecipientsChange?: (recipients: NotificationRecipient[]) => void;
}

interface AllUser extends NotificationRecipient {
  isSelected: boolean;
}

const NotificationSettingsModal: React.FC<NotificationSettingsModalProps> = ({
  isOpen,
  onClose,
  productId,
  canManage,
  onRecipientsChange,
}) => {
  const [recipients, setRecipients] = useState<NotificationRecipient[]>([]);
  const [availableUsers, setAvailableUsers] = useState<NotificationRecipient[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'add' | 'remove'>('add');
  const [searchTerm, setSearchTerm] = useState('');
  // メンバー追加ピッカーの開閉（既存の検索＋追加機能をこの中に収める）
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  // 競合通知トグル: 対応する既存フィールドが無いため視覚のみのローカルstate（保存対象にしない）
  const [competitorNotify, setCompetitorNotify] = useState(true);

  // Fetch notification recipients
  const fetchRecipients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest.get<{ data: NotificationRecipient[] }>(
        `knowledge/products/${productId}/notification-recipients`
      );
      const recipientData = response.data || [];
      setRecipients(recipientData);
      onRecipientsChange?.(recipientData);
    } catch (err: any) {
      console.error('Failed to fetch notification recipients:', err);
      setError('通知先の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [productId, onRecipientsChange]);

  // Fetch available users
  const fetchAvailableUsers = useCallback(async () => {
    try {
      const response = await apiRequest.get<{ data: NotificationRecipient[] }>(
        `knowledge/products/${productId}/available-recipients`
      );
      setAvailableUsers(response.data || []);
    } catch (err: any) {
      console.error('Failed to fetch available users:', err);
      setError('ユーザーの取得に失敗しました');
    }
  }, [productId]);

  // Initial fetch when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchRecipients();
      fetchAvailableUsers();
      setSearchTerm('');
      setIsPickerOpen(false);
    }
  }, [isOpen, fetchRecipients, fetchAvailableUsers]);

  // Combine recipients and available users into a single list
  const allUsers = useMemo<AllUser[]>(() => {
    // Combine all users
    const combined: AllUser[] = [
      ...recipients.map((r) => ({ ...r, isSelected: true })),
      ...availableUsers.map((u) => ({ ...u, isSelected: false })),
    ];

    // Remove duplicates (in case available-recipients API includes current recipients)
    const uniqueUsers = combined.filter(
      (user, index, self) => index === self.findIndex((u) => u.id === user.id)
    );

    return uniqueUsers;
  }, [recipients, availableUsers]);

  // Filter users by search term
  const filteredUsers = useMemo(() => {
    if (searchTerm.trim() === '') {
      return allUsers;
    }
    const term = searchTerm.toLowerCase();
    return allUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term)
    );
  }, [searchTerm, allUsers]);

  // 追加ピッカーに出す候補（未追加のユーザーのみ）
  const candidateUsers = useMemo(
    () => filteredUsers.filter((user) => !user.isSelected),
    [filteredUsers]
  );

  // Handle adding recipient
  const handleAddRecipient = async (userId: number) => {
    try {
      setError(null);
      setSuccessMessage(null);

      const response = await apiRequest.post<{ data: NotificationRecipient; message: string }>(
        `knowledge/products/${productId}/notification-recipients`,
        { user_id: userId }
      );

      const newRecipient = response.data;
      const updatedRecipients = [...recipients, newRecipient];
      setRecipients(updatedRecipients);
      onRecipientsChange?.(updatedRecipients);

      // Remove from available users
      setAvailableUsers((prev) => prev.filter((user) => user.id !== userId));

      setMessageType('add');
      setSuccessMessage('通知先ユーザーを追加しました');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to add notification recipient:', err);
      const errorMessage = err.response?.data?.message || '通知先の追加に失敗しました';
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle removing recipient
  const handleRemoveRecipient = async (userId: number) => {
    try {
      setError(null);
      setSuccessMessage(null);

      await apiRequest.delete<{ message: string }>(
        `knowledge/products/${productId}/notification-recipients/${userId}`
      );

      const removedUser = recipients.find((r) => r.id === userId);
      const updatedRecipients = recipients.filter((recipient) => recipient.id !== userId);
      setRecipients(updatedRecipients);
      onRecipientsChange?.(updatedRecipients);

      // Add back to available users
      if (removedUser) {
        setAvailableUsers((prev) => [...prev, removedUser]);
      }

      setMessageType('remove');
      setSuccessMessage('通知先ユーザーを解除しました');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to remove notification recipient:', err);
      const errorMessage = err.response?.data?.message || '通知先の解除に失敗しました';
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  if (!isOpen) return null;

  // モック 24px アバター丸（primary地・白文字）
  const renderAvatar = (name: string) => (
    <div className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary text-[11px] font-bold text-white">
      {getInitials(name)}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[rgba(20,18,28,.42)] p-6"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-[440px] overflow-hidden rounded-[18px] bg-white shadow-[0_30px_80px_rgba(0,0,0,.3)]"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="通知先"
      >
        <h3 className="flex items-center gap-2 px-[22px] pt-[18px] text-[16px] font-extrabold text-[#1D1927]">
          <span className="material-icons-round text-[19px] text-primary">notifications</span>
          通知先
        </h3>
        <div className="px-[22px] pt-3 text-[13px] leading-[1.7] text-[#4A4D60]">
          {/* Success/Error Messages（既存機能を保持・器のみモック調に） */}
          {successMessage && (
            <div
              className={`mb-2 rounded-lg px-3 py-[6px] text-[11.5px] ${
                messageType === 'add'
                  ? 'bg-[#D1FAE5] text-[#065F46]'
                  : 'bg-[#DBEAFE] text-[#1E40AF]'
              }`}
            >
              {successMessage}
            </div>
          )}
          {error && (
            <div className="mb-2 rounded-lg bg-[#FEE2E2] px-3 py-[6px] text-[11.5px] text-[#991B1B]">
              {error}
            </div>
          )}

          <div className="mb-1 text-[11.5px] font-bold text-[#6B7280]">通知を受け取るメンバー</div>

          {loading ? (
            <div className="flex items-center gap-2 py-[6px] text-[12px] text-[#8A90A4]">
              <span className="material-icons-round inline-block animate-spin text-[15px] text-primary">sync</span>
              読み込み中...
            </div>
          ) : recipients.length === 0 ? (
            <div className="py-[6px] text-[12px] text-[#8A90A4]">通知先メンバーはまだいません</div>
          ) : (
            recipients.map((user) => (
              <div key={user.id} className="flex items-center gap-2 py-[6px] text-[12.5px]">
                {renderAvatar(user.name)}
                <span className="min-w-0 flex-1 truncate">
                  {user.name}
                  {user.is_default && (
                    <span className="ml-[6px] text-[10px] font-bold text-[#8A90A4]">デフォルト</span>
                  )}
                </span>
                {canManage && !user.is_default && (
                  <button
                    type="button"
                    onClick={() => handleRemoveRecipient(user.id)}
                    title="通知先から解除"
                    aria-label={`${user.name} を通知先から解除`}
                    className="flex flex-none items-center text-[#C4C0B4] transition-colors hover:text-[#C96162]"
                  >
                    <span className="material-icons-round text-[16px]">close</span>
                  </button>
                )}
              </div>
            ))
          )}

          {canManage ? (
            <button
              type="button"
              onClick={() => setIsPickerOpen((open) => !open)}
              className="flex items-center gap-[6px] py-[3px] text-[12px] font-semibold text-primary"
            >
              <span className="material-icons-round text-[16px]">add</span>
              メンバーを追加
            </button>
          ) : (
            <div className="py-[3px] text-[10.5px] text-[#8A90A4]">（管理者のみ編集可能）</div>
          )}

          {canManage && isPickerOpen && (
            <div className="mt-2 rounded-[10px] border border-[#E8E6E0] p-2">
              <div className="relative mb-2">
                <span className="material-icons-round pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[15px] text-[#9CA3AF]">search</span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="ユーザー名またはメールアドレスで検索"
                  className="box-border w-full rounded-[9px] border-[1.5px] border-[#E4E4E7] py-[7px] pl-8 pr-3 text-[12px] outline-none [font-family:inherit] focus:border-primary"
                />
              </div>
              <div className="max-h-[168px] overflow-y-auto">
                {candidateUsers.length === 0 ? (
                  <div className="px-2 py-[6px] text-[11.5px] text-[#8A90A4]">
                    {searchTerm.trim() !== ''
                      ? '検索結果が見つかりませんでした'
                      : '追加できるユーザーがいません'}
                  </div>
                ) : (
                  candidateUsers.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleAddRecipient(user.id)}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-[6px] text-left transition-colors hover:bg-[rgba(97,111,201,.06)]"
                    >
                      {renderAvatar(user.name)}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[12.5px] text-[#1D1927]">{user.name}</span>
                        <span className="block truncate text-[10.5px] text-[#8A90A4]">{user.email}</span>
                      </span>
                      <span className="material-icons-round flex-none text-[16px] text-primary">add</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="mt-3 flex items-center gap-[10px] border-t border-[#E8E6E0] pt-[14px]">
            <span className="material-icons-round text-[20px] text-[#C9619C]">compare</span>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] font-bold text-[#1D1927]">競合情報の変更を通知</div>
              <div className="text-[10.5px] leading-[1.6] text-[#8A90A4]">競合の追加・価格変動・新規参入を検知したら知らせる</div>
            </div>
            {/* 視覚トグル（モック .toggle 準拠 40x22）。保存APIに対応フィールドが無いため保存しない */}
            <button
              type="button"
              role="switch"
              aria-checked={competitorNotify}
              aria-label="競合情報の変更を通知"
              onClick={() => setCompetitorNotify((value) => !value)}
              className={`relative h-[22px] w-10 flex-none cursor-pointer rounded-full transition-colors ${
                competitorNotify ? 'bg-[#44C2A4]' : 'bg-[#D4D4D8]'
              }`}
            >
              <span
                className={`absolute top-[2px] h-[18px] w-[18px] rounded-full bg-white transition-all ${
                  competitorNotify ? 'left-5' : 'left-[2px]'
                }`}
              />
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-[9px] px-[22px] pb-5 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-[6px] rounded-lg bg-primary px-[13px] py-[6px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-hover"
          >
            完了
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsModal;
